import { useMemo } from 'react'
import { useResource } from './useResource'
import { words } from './format'

// ======================================================================
// THE TENANT'S OWN WORD FOR EACH CASE ROLE (task 27 part C)
// ======================================================================
//
// An organisation calls the decision maker a "Hearing Manager", or the
// reporter a "Complainant". Same nine roles, same nine capability sets,
// the tenant's own vocabulary on the screen.
//
// ======================================================================
// THE KEY IS WHAT TRAVELS. THE LABEL IS WHAT IS SHOWN.
// ======================================================================
//
// Every API response in this module carries the KEY — 'decision_maker' —
// because that is what er_case_parties stores, what acl.CAPABILITIES is
// keyed on, and what every access decision on the server reads. Nothing
// in this application ever sends a label back, matches on one, or
// derives anything from one.
//
// So the label is applied HERE, at render, from one map fetched once
// per screen. There is deliberately no list of nine labels anywhere in
// the frontend: the map comes from GET /api/cases/case-roles, resolved
// server-side against the caller's own organisation from
// er_case_role_labels with acl.DEFAULT_ROLE_LABELS underneath. A copy
// in the client would be a second definition of the tenant's vocabulary
// that goes stale the moment somebody renames a role — on exactly the
// screens where the rename was the point.
//
// ======================================================================
// THE AUDIT TRAIL RENDERS UNDER THE CURRENT LABEL, AND THAT IS A CHOICE
// ======================================================================
//
// A party_granted row written last March stores case_role:
// 'decision_maker'. If the organisation has since renamed that role to
// "Hearing Manager", this renders the row as "Hearing Manager".
//
// THE RECORD IS UNCHANGED BY THAT. What the chain stores, hashes and
// verifies is the KEY, and the key has not moved — the audit trail is
// as true after the rename as before it. What moved is the word this
// organisation uses for that role, and a trail that rendered the old
// word would be showing a name that no longer exists anywhere else in
// the product: the party list, the case header and the granting dialog
// would all say "Hearing Manager" while the history said "Decision
// maker", and a reader would reasonably conclude they were two
// different things.
//
// THE HONEST ENGINEERING FACT UNDERNEATH IT: the old label is not
// stored anywhere. er_case_role_labels holds one current label per
// role, and the audit row holds the key. Rendering "the label as it was
// at the time" would require versioning the labels — which is a real
// design, and the right one if a tenant ever needs to prove what a
// screen said on a date, but it is not what exists and pretending
// otherwise by showing a default would be worse than either.

/** The org's label map, and helpers over it.
 *
 *  ONE FETCH PER SCREEN, shared by every component below it through the
 *  props they are already given. useResource caches per path for the
 *  life of the mount, so several callers on one screen do not multiply
 *  the request.
 *
 *  IT DEGRADES TO words(). If the fetch has not landed or failed, every
 *  role renders exactly as it did before this existed —
 *  'decision_maker' as "decision maker" — which is the module's own
 *  default in all but capitalisation and is never wrong, only generic.
 *  A case screen must not wait on a vocabulary lookup, and must not
 *  break when one fails. */
export function useRoleLabels() {
  const { data } = useResource('/case-roles')

  return useMemo(() => {
    const map = {}
    for (const r of data?.roles || []) map[r.key] = r.label

    /** One role key as this organisation names it. */
    const label = (key) => map[key] || words(key)

    /** A list of keys, joined as this organisation names them. Used for
     *  "You are ..." on the case list, where somebody can hold more
     *  than one role on a case. */
    const labelList = (keys) =>
      (Array.isArray(keys) ? keys : [keys]).filter(Boolean).map(label).join(', ')

    return { map, label, labelList, roles: data?.roles || [] }
  }, [data])
}
